import { renderRowsWithSpacing } from "@/lib/accordion-content";
import type { Auction } from "@/lib/types";

/** Minimal history row for provenance rendering (API + FetchedMultipleprint history). */
export type MultipleprintProvenanceHistoryInput = {
  id?: string;
  id_imported?: number;
  provenanceRank?: number;
  multipleprintId?: string | null;
  estampesMultiplesHistoriqueDescriptionNom?: string;
  estampesMultiplesHistoriqueDescriptionVille?: string;
  estampesMultiplesHistoriqueDescriptionDates?: string;
  estampesMultiplesHistoriqueDescriptionCollectionPart?: boolean;
};

/** Minimal multiple-in-auction row for provenance rendering (MultipleprintInAuction satisfies this). */
export type MultipleprintProvenanceAuctionInput = {
  id?: string;
  provenanceRank?: number;
  ventesDansEstampesMultiplesDescriptionNumeroLot?: string;
  auction?: Auction | null;
};

type ProvenanceRow =
  | { kind: "history"; index: number; item: MultipleprintProvenanceHistoryInput }
  | {
      kind: "auction";
      index: number;
      item: MultipleprintProvenanceAuctionInput;
    };

const cleanParts = (parts: Array<string | null | undefined>) =>
  parts.map((part) => part?.trim()).filter(Boolean) as string[];

const hasPersistedOrder = (rows: ProvenanceRow[]) =>
  rows.some((row) => (row.item.provenanceRank ?? 0) > 0);

const getFallbackOrder = (row: ProvenanceRow, historyCount: number) => {
  if (row.kind === "history") {
    return row.item.id_imported ?? row.index;
  }
  return historyCount + row.index;
};

export function buildMultipleprintProvenance({
  history,
  auctions,
  t,
}: {
  history?: MultipleprintProvenanceHistoryInput[];
  auctions?: MultipleprintProvenanceAuctionInput[];
  t: (key: string) => string;
}) {
  const historyRows: ProvenanceRow[] = (history ?? []).map((item, index) => ({
    kind: "history",
    item,
    index,
  }));
  const auctionRows: ProvenanceRow[] = (auctions ?? []).map((item, index) => ({
    kind: "auction",
    item,
    index,
  }));
  const rows = [...historyRows, ...auctionRows];
  if (rows.length === 0) return null;

  const shouldUsePersistedOrder = hasPersistedOrder(rows);
  rows.sort((a, b) => {
    const aRank = shouldUsePersistedOrder
      ? (a.item.provenanceRank ?? 0)
      : getFallbackOrder(a, historyRows.length);
    const bRank = shouldUsePersistedOrder
      ? (b.item.provenanceRank ?? 0)
      : getFallbackOrder(b, historyRows.length);
    if (aRank !== bRank) return aRank - bRank;
    if (a.kind !== b.kind) return a.kind === "history" ? -1 : 1;
    return (a.item.id ?? "").localeCompare(b.item.id ?? "");
  });

  const renderedRows = rows
    .map((row) => {
      if (row.kind === "history") {
        const item = row.item;
        const location = item.estampesMultiplesHistoriqueDescriptionCollectionPart
          ? t("computeField.collectionParticuliere")
          : item.estampesMultiplesHistoriqueDescriptionNom;
        return cleanParts([
          location,
          item.estampesMultiplesHistoriqueDescriptionVille,
          item.estampesMultiplesHistoriqueDescriptionDates,
        ]).join(", ");
      }

      const item = row.item;
      const auction = item.auction;
      const location = cleanParts([
        auction?.ventesDescriptionCommissaires,
        auction?.ventesDescriptionLieu,
        auction?.ventesDescriptionVille,
        auction?.ventesDescriptionPays,
        item.ventesDansEstampesMultiplesDescriptionNumeroLot
          ? `lot n°${item.ventesDansEstampesMultiplesDescriptionNumeroLot}`
          : null,
      ]).join(", ");
      return cleanParts([auction?.ventesDescriptionDate, location]).join(" - ");
    })
    .filter(Boolean);

  return renderedRows.length > 0 ? renderRowsWithSpacing(renderedRows) : null;
}
